import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FontFamily } from '@/enums/index';
import { BREAKPOINTS } from '@/styles/themes/default';

import { ParagraphContainer, Heading, LandingContainer } from './Common';
import { TestimonialCard } from './TestimonialCard';

const MessageContainer = styled(ParagraphContainer)`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    grid-column: 1 / 5;
  }

  p {
    font-family: ${FontFamily.IBMPlexSans};
    font-size: 16px;
    line-height: 24px;

    @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
      font-size: 20px;
      line-height: 135%;
    }
  }
`;

const CardsContainer = styled.div`
  position: relative;
  min-height: 256px;

  height: 280px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    height: 256px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    height: 340px;
    padding-top: 40px;
  }
`;

const GridContainer = styled(LandingContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'message'
    'cards';
  grid-row-gap: 40px;

  ${Heading} {
    margin-bottom: 32px;
  }

  ${MessageContainer} {
    grid-area: message;
  }

  ${CardsContainer} {
    grid-area: cards;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    grid-template-columns: repeat(12, 1fr);
    grid-template-areas: 'cards message';

    ${MessageContainer} {
      grid-column: 8 / -1;
    }

    ${CardsContainer} {
      grid-column: 1 / 7;
    }
  }
`;

export const AttractSpeakers = () => {
  const { t } = useTranslation();
  const [showTestimonialIndex, setShowTestimonialIndex] = useState(0);

  const testimonials: Array<{ image: string; name: string; affiliation: string; text: string }> = [
    {
      name: 'Thore Graepel',
      image: 'images/landing/testimonials/thore.png',
      affiliation: t('landing.attractSpeakers.testemonial1Affiliation'),
      text: t('landing.attractSpeakers.testemonial1Text'),
    },
    {
      name: 'Sarvapali D. Ramchurn',
      image: 'images/landing/testimonials/sarvapali.png',
      affiliation: t('landing.attractSpeakers.testemonial2Affiliation'),
      text: t('landing.attractSpeakers.testemonial2Text'),
    },
    {
      name: 'Robert Riener',
      image: 'images/landing/testimonials/robert.png',
      affiliation: t('landing.attractSpeakers.testemonial3Affiliation'),
      text: t('landing.attractSpeakers.testemonial3Text'),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      const nextTestimonialIndex = (showTestimonialIndex + 1) % testimonials.length;

      setShowTestimonialIndex(nextTestimonialIndex);
    }, 5000);

    return () => clearInterval(interval);
  }, [showTestimonialIndex, setShowTestimonialIndex, testimonials.length]);

  return (
    <GridContainer>
      <MessageContainer>
        <Heading>{t('landing.attractSpeakers.title')}</Heading>
        <p>{t('landing.attractSpeakers.description')}</p>
        <p>{t('landing.attractSpeakers.audienceInsight')}</p>
      </MessageContainer>
      <CardsContainer>
        {testimonials.map((testimonial, index) => (
          <TestimonialCard testimonial={testimonial} key={testimonial.name} visible={index === showTestimonialIndex} />
        ))}
      </CardsContainer>
    </GridContainer>
  );
};
